/**
 * Where an input's text must start when the field carries a static `prefix_text`.
 *
 * 🚨 **The gutter used to be a fixed 40px — the width of an ICON**, and a prefix is arbitrary
 * text. Anything wider than the gutter ran underneath whatever came next, on both bundles and in
 * two different ways:
 *
 *  - NubeSDK / the Designer preview: the input took `fieldInputWithIcon`'s 40px padding and the
 *    placeholder overlay sat at `left: 40px`, while the prefix was drawn from `left: 12px`. A
 *    six-character prefix ends past 40px, so it overlapped the placeholder.
 *  - Legacy storefront: worse. `.cod-input.cod-has-prefix` set `padding-left: 12px` and
 *    `.cod-prefix` was also at `left: 12px`, so the prefix sat exactly on top of the native
 *    placeholder AND on the buyer's typed value.
 *
 * Both reported from a live store. Neither renderer can measure text — `form-tree` is pure and the
 * SDK bundle runs in a Web Worker with no DOM — so the width is expressed in **`ch`** and left to
 * the browser to resolve in whatever font the field ends up using. `ch` is the advance of "0", so
 * for a digit prefix like `+57` it is exact, and for letters it over-estimates slightly, which is
 * the safe direction: a little too much padding looks fine, too little overlaps.
 *
 * Shared rather than duplicated for the usual reason — the two bundles ship at once, and a prefix
 * that clears the text on one theme and not another is the same store looking broken on half its
 * traffic.
 */
/** Where the gutter starts: the icon's own 40px when there is one, else the input's 12px padding. */
const ICON_GUTTER_PX = 40;
const TEXT_START_PX = 12;
/** Breathing room between the prefix and the value, so they read as two things. */
const GAP_PX = 6;
/**
 * The `padding-left` an input needs to clear its prefix, or `null` when it has none.
 *
 * Returned as a CSS length string (a `calc()`), usable in a `padding` shorthand, a `left`, or an
 * inline style — every consumer needs it in a slightly different place.
 */
export function prefixGutter(prefixText, hasIcon) {
    const text = prefixText ?? '';
    if (!text)
        return null;
    const start = hasIcon ? ICON_GUTTER_PX : TEXT_START_PX;
    return `calc(${start}px + ${text.length}ch + ${GAP_PX}px)`;
}
/** Where the prefix itself is drawn: the start of the gutter, after any icon. */
export function prefixLeft(hasIcon) {
    return `${hasIcon ? ICON_GUTTER_PX : TEXT_START_PX}px`;
}
//# sourceMappingURL=prefix.js.map